import { useState, useEffect } from "react";

/**
 * A React hook that tracks whether a CSS media query matches.
 *
 * @param query - The CSS media query string to evaluate (e.g., "(min-width: 768px)")
 * @param defaultValue - The default value to return on server-side rendering or when matchMedia is unavailable
 * @returns boolean indicating whether the media query currently matches
 *
 * @example
 * ```tsx
 * const isMobile = useMediaQuery("(max-width: 767px)");
 * const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
 * const isDesktop = useMediaQuery("(min-width: 1024px)");
 * ```
 */
export function useMediaQuery(query: string, defaultValue = false): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    // Check if we're in a browser environment
    if (typeof window === "undefined" || typeof window.matchMedia === "undefined") {
      return defaultValue;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    // Early return for SSR or non-browser environments
    if (typeof window === "undefined" || typeof window.matchMedia === "undefined") {
      return;
    }

    const mediaQueryList = window.matchMedia(query);

    // Set initial value
    setMatches(mediaQueryList.matches);

    // Event handler for media query changes
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Add event listener
    mediaQueryList.addEventListener("change", handleChange);

    // Cleanup
    return () => {
      mediaQueryList.removeEventListener("change", handleChange);
    };
  }, [query]);

  return matches;
}

/**
 * Common breakpoint queries matching Tailwind CSS defaults
 */
export const breakpoints = {
  sm: "(min-width: 640px)",
  md: "(min-width: 768px)",
  lg: "(min-width: 1024px)",
  xl: "(min-width: 1280px)",
  "2xl": "(min-width: 1536px)",
} as const;

/**
 * Convenience hooks for common breakpoints
 */
export function useIsMobile(): boolean {
  return !useMediaQuery(breakpoints.md);
}

export function useIsTablet(): boolean {
  const isAboveMd = useMediaQuery(breakpoints.md);
  const isAboveLg = useMediaQuery(breakpoints.lg);
  return isAboveMd && !isAboveLg;
}

export function useIsDesktop(): boolean {
  return useMediaQuery(breakpoints.lg);
}
